"use client";

import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { downloadReport } from "@/lib/report-download";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

type G3 = {
  period: { month: number; year: number };
  outward_taxable: number;
  outward_cgst: number;
  outward_sgst: number;
  outward_igst: number;
  itc_cgst: number;
  itc_sgst: number;
  itc_igst: number;
  itc_reversal: number;
  net_tax_payable: number;
};

export default function Gstr3bPage() {
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));

  const { data, refetch } = useQuery({
    queryKey: ["reports", "gstr3b", month, year],
    queryFn: async () => {
      const { data: d } = await api.get<G3>("/api/reports/gstr3b", {
        params: { month: Number(month), year: Number(year) },
      });
      return d;
    },
  });

  return (
    <div className="space-y-6 p-6 max-w-xl">
      <Link
        to="/reports"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
      >
        <ArrowLeft className="h-4 w-4" />
        Reports
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">GSTR-3B summary</h1>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-end gap-4 justify-between">
          <div className="flex gap-4">
            <div className="grid gap-1.5">
              <Label className="text-xs">Month</Label>
              <Input type="number" min={1} max={12} className="w-24" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Year</Label>
              <Input className="w-28" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <Button type="button" variant="secondary" className="mt-auto" onClick={() => void refetch()}>
              Load
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            className="mt-auto"
            onClick={() =>
              void downloadReport(
                "/api/reports/gstr3b/export",
                { month: Number(month), year: Number(year) },
                `gstr3b-${year}-${month}.xlsx`,
              )
            }
          >
            Excel
          </Button>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {data ? (
            <>
              <Row k="Outward taxable" v={data.outward_taxable} />
              <Row k="Outward CGST / SGST / IGST" v={`${data.outward_cgst} / ${data.outward_sgst} / ${data.outward_igst}`} />
              <Row k="ITC CGST / SGST / IGST" v={`${data.itc_cgst} / ${data.itc_sgst} / ${data.itc_igst}`} />
              <Row k="ITC reversal" v={data.itc_reversal} />
              <Row k="Net tax payable (simplified)" v={data.net_tax_payable} bold />
            </>
          ) : (
            <p className="text-muted-foreground">Loading…</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: string | number; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/60 py-2">
      <span className="text-muted-foreground">{k}</span>
      <span className={cn("tabular-nums", bold && "font-semibold")}>{typeof v === "number" ? v.toFixed(2) : v}</span>
    </div>
  );
}
